import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useRole } from "@/hooks/useRole";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import OnboardingWizard from "@/components/OnboardingWizard";
import Dashboard from "@/components/Dashboard";
import ChatWidget from "@/components/ChatWidget";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CheckEmail from "@/pages/CheckEmail";
import Settings from "@/pages/Settings";
import Embed from "@/pages/Embed";
import FindReferrals from "@/pages/FindReferrals";
import LeadFilters from "@/pages/LeadFilters";
import ReferralOutreach from "@/pages/ReferralOutreach";
import Analytics from "@/pages/Analytics";
import Reports from "@/pages/Reports";
import Community from "@/pages/Community";
import QuickIntake from "@/pages/QuickIntake";
import Appointments from "@/pages/Appointments";
import NotFound from "@/pages/NotFound";
import AuditLog from "@/pages/AuditLog";
import LandingPage from "@/pages/LandingPage";
import LeadsPage from "@/pages/LeadsPage";
import AdminPortal from "@/pages/AdminPortal";
import Unauthorized from "@/pages/Unauthorized";
import AdminRoute from "@/components/AdminRoute";
import SuperAdminRoute from "@/components/SuperAdminRoute";
import StaffTasks from "@/pages/StaffTasks";
import MyProfile from "@/pages/MyProfile";
import TeamManagement from "@/pages/TeamManagement";
import OwnerAnalytics from "@/pages/OwnerAnalytics";
import RoleRoute from "@/components/RoleRoute";
import FeatureGate from "@/components/FeatureGate";
import EmailPreview from "@/pages/EmailPreview";
import BusinessIntelligence from "@/pages/admin/BusinessIntelligence";
import Pricing from "@/pages/Pricing";
import Affiliate from "@/pages/Affiliate";
import ApiDocs from "@/pages/ApiDocs";
import ApiExplorer from "@/pages/ApiExplorer";
import SeoManager from "@/pages/SeoManager";
import VisitorTracking from "@/pages/VisitorTracking";
import ReviewsReputation from "@/pages/ReviewsReputation";
import MarketingToolkit from "@/pages/MarketingToolkit";
import PartnerPerformance from "@/pages/PartnerPerformance";
import GoogleBusiness from "@/pages/GoogleBusiness";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import HipaaNotice from "@/pages/legal/HipaaNotice";
import ErrorBoundary from "@/components/ErrorBoundary";
import InactivityWarning from "@/components/InactivityWarning";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const queryClient = new QueryClient();

function AppRoutes() {
  const { user, loading: authLoading } = useAuth();
  const { isOnboarded, loading: profileLoading } = useProfile();
  const { isAdmin, loading: roleLoading } = useRole();
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const location = useLocation();
  useDocumentTitle();

  // Reset the "just finished onboarding" override when the signed-in user
  // changes, so it doesn't leak across accounts in the same tab.
  useEffect(() => {
    setOnboardingComplete(false);
  }, [user?.id]);

  const isAdminPath = location.pathname.startsWith("/admin");
  // Until auth/profile/role have all resolved for a signed-in user, we don't
  // yet know whether they need onboarding — show a spinner instead of
  // falling through to the routes, which would otherwise flash the
  // dashboard before redirecting to the onboarding wizard (e.g. right after
  // a fresh Google sign-in for a brand-new account).
  const decidingAccess = !!user && !isAdminPath && (authLoading || profileLoading || roleLoading);
  const showOnboarding = !!user && !isAdminPath && !decidingAccess && !isAdmin && !isOnboarded && !onboardingComplete;

  if (decidingAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (showOnboarding) {
    return <OnboardingWizard onComplete={() => setOnboardingComplete(true)} />;
  }

  if (!authLoading && !user && location.pathname === "/") {
    return <LandingPage />;
  }

  return (
    <>
      <Routes>
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/affiliate" element={<Affiliate />} />
        <Route path="/api-docs" element={<ApiDocs />} />
        <Route path="/api-docs/explorer" element={<ApiExplorer />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/hipaa" element={<HipaaNotice />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/admin" element={<AdminRoute><AdminPortal /></AdminRoute>} />
        <Route path="/admin/email-preview" element={<AdminRoute><EmailPreview /></AdminRoute>} />
        <Route path="/admin/business-intelligence" element={<SuperAdminRoute><BusinessIntelligence /></SuperAdminRoute>} />
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="/leads" element={<RoleRoute permission="accessLeads"><LeadsPage /></RoleRoute>} />
          <Route path="/referrals" element={<FindReferrals />} />
          <Route path="/lead-filters" element={<LeadFilters />} />
          <Route path="/outreach" element={<ReferralOutreach />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/community" element={<RoleRoute permission="viewReferralOutreach"><Community /></RoleRoute>} />
          <Route path="/quick-intake" element={<QuickIntake />} />
          <Route path="/appointments" element={<Appointments />} />
          <Route path="/settings" element={<RoleRoute permission="viewHipaaSettings"><Settings /></RoleRoute>} />
          <Route path="/audit-log" element={<RoleRoute permission="viewHipaaSettings"><AuditLog /></RoleRoute>} />
          <Route path="/embed" element={<RoleRoute permission="viewChatbotEmbed"><Embed /></RoleRoute>} />
          <Route path="/tasks" element={<RoleRoute permission="viewTaskView"><StaffTasks /></RoleRoute>} />
          <Route path="/profile" element={<MyProfile />} />
          <Route path="/team" element={<RoleRoute permission="manageTeam"><TeamManagement /></RoleRoute>} />
          <Route path="/analytics-owner" element={<RoleRoute permission="viewAnalytics"><OwnerAnalytics /></RoleRoute>} />
          <Route path="/seo" element={
            <RoleRoute permission="viewGrowthMarketing">
              <FeatureGate
                flag="growthMarketing"
                feature="SEO Manager"
                description="Track keyword rankings, on-page health, and local SEO performance from one dashboard."
                highlights={[
                  "Local keyword rank tracking",
                  "On-page SEO audits for your site",
                  "Competitor visibility comparison",
                ]}
                source="growth_seo_manager"
              >
                <SeoManager />
              </FeatureGate>
            </RoleRoute>
          } />
          <Route path="/visitors" element={
            <RoleRoute permission="viewGrowthMarketing">
              <FeatureGate
                flag="growthMarketing"
                feature="Visitor Tracking"
                description="See which families and partners visit your site, what they read, and when to follow up."
                highlights={[
                  "Anonymous visitor identification",
                  "Page-level engagement signals",
                  "Hot-lead alerts for repeat visitors",
                ]}
                source="growth_visitor_tracking"
              >
                <VisitorTracking />
              </FeatureGate>
            </RoleRoute>
          } />
          <Route path="/reviews" element={
            <RoleRoute permission="viewGrowthMarketing">
              <FeatureGate
                flag="growthMarketing"
                feature="Reviews & Reputation"
                description="Monitor reviews across Google, Yelp, and Caring.com, and respond from one inbox."
                highlights={[
                  "Unified review inbox",
                  "Automated review request campaigns",
                  "AI-suggested responses",
                ]}
                source="growth_reviews"
              >
                <ReviewsReputation />
              </FeatureGate>
            </RoleRoute>
          } />
          <Route path="/toolkit" element={
            <RoleRoute permission="viewGrowthMarketing">
              <FeatureGate
                flag="growthMarketing"
                feature="Marketing Toolkit"
                description="Ready-to-send brochures, social posts, and outreach templates branded for your agency."
                highlights={[
                  "Branded one-pagers and brochures",
                  "Social post library with AI variants",
                  "Email outreach templates by audience",
                ]}
                source="growth_marketing_toolkit"
              >
                <MarketingToolkit />
              </FeatureGate>
            </RoleRoute>
          } />
          <Route path="/partners" element={
            <RoleRoute permission="viewGrowthMarketing">
              <FeatureGate
                flag="growthMarketing"
                feature="Partner Performance"
                description="Track referrals, conversions, and revenue by referral partner so you double down on what works."
                highlights={[
                  "Referral source attribution",
                  "Partner health scoring",
                  "Revenue-by-partner reporting",
                ]}
                source="growth_partner_performance"
              >
                <PartnerPerformance />
              </FeatureGate>
            </RoleRoute>
          } />
          <Route path="/gbp" element={
            <RoleRoute permission="viewGrowthMarketing">
              <FeatureGate
                flag="growthMarketing"
                feature="Google Business Profile"
                description="Manage your Google Business Profile, posts, and Q&A directly from LegacyGrowth Engine."
                highlights={[
                  "Schedule GBP posts in advance",
                  "Track profile views and calls",
                  "Reply to questions and reviews",
                ]}
                source="growth_google_business"
              >
                <GoogleBusiness />
              </FeatureGate>
            </RoleRoute>
          } />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      {user && <ChatWidget />}
      <InactivityWarning />
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
